import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { storeSet, storeRemove, storeGetJSON, storeSetJSON } from "../lib/nativeStore";

const AuthContext = createContext(null);

const CACHED_USER_KEY = "scanix_cached_user";
const TOKEN_KEY = "scanix_token";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, object = user, false = anon
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/me", { timeout: 8000 });
                setUser(data);
                await storeSetJSON(CACHED_USER_KEY, data);
            } catch (err) {
                // A network-level failure (no response at all — no signal, DNS failure,
                // timeout) doesn't mean the session is invalid, it means we can't check.
                // Operator buses run on hill-station routes with unreliable signal, so falling
                // back to the last-known identity here (rather than bouncing to /login) is what
                // keeps the app usable on a cold start with no network. A real 401/403 from the
                // server is the only thing that actually signs the user out.
                if (!err?.response) {
                    const cached = await storeGetJSON(CACHED_USER_KEY, null);
                    setUser(cached || false);
                } else {
                    await storeRemove(CACHED_USER_KEY);
                    setUser(false);
                }
            } finally {
                setChecking(false);
            }
        })();
    }, []);

    const login = async (identifier, password) => {
        const { data } = await api.post("/auth/login", { identifier, password });
        if (data.token) await storeSet(TOKEN_KEY, data.token);
        setUser(data.user);
        await storeSetJSON(CACHED_USER_KEY, data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            // ignore
        }
        await storeRemove(TOKEN_KEY);
        await storeRemove(CACHED_USER_KEY);
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, checking, login, logout, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
